import * as Sentry from "@sentry/react";
import { Button, Form, Input, Modal, Spin, Table } from "antd";
import type { InputRef } from "antd";
import type { FormInstance } from "antd/es/form";
import React, { useState } from "react";
import { useProject } from "../../App/state";
import { useNotification } from "../../contexts/Notifications";
import {
  useGetSubcontractorsQuery,
  useUpdateSubcontractorMutation,
} from "../../generated/types";

type TableData = {
  id: string;
  name: string;
  displayName: string;
  key: string;
};

const COLUMNS = [
  {
    title: "",
    dataIndex: "displayName",
    key: "displayName",
    editable: true,
  },
];

const EditableContext = React.createContext<FormInstance<any> | null>(null);

interface Item {
  key: string;
  id: string;
  name: string;
  displayName: string;
}

const EditableRow = (props) => {
  const [form] = Form.useForm();
  return (
    <Form form={form} component={false}>
      <EditableContext.Provider value={form}>
        <tr {...props} />
      </EditableContext.Provider>
    </Form>
  );
};

interface EditableCellProps {
  title: React.ReactNode;
  editable: boolean;
  children: React.ReactNode;
  dataIndex: keyof Item;
  record: Item;
  handleSave: (record: Item) => void;
}

const EditableCell: React.FC<EditableCellProps> = (
  props: EditableCellProps,
) => {
  const { title, editable, children, dataIndex, record, handleSave } = props;
  const [editing, setEditing] = React.useState(false);
  const [showConfirmModal, setShowConfirmModal] = React.useState(false);
  const inputRef = React.useRef<InputRef>(null);
  const form = React.useContext(EditableContext)!;

  React.useEffect(() => {
    if (editing) {
      inputRef.current!.focus();
    }
  }, [editing]);

  const toggleEdit = () => {
    setEditing(!editing);
    form.setFieldsValue({ [dataIndex]: record[dataIndex] });
  };

  const cancel = () => {
    if (!showConfirmModal) {
      toggleEdit();
    }
  };

  const save = async () => {
    try {
      const values = await form.validateFields();
      const updatedRecord = { ...record, ...values };

      setShowConfirmModal(false);
      toggleEdit();
      handleSave(updatedRecord);
    } catch (errInfo) {
      setShowConfirmModal(false);
      Sentry.captureException(errInfo, {
        tags: {
          component: "SubcontractorsList",
          action: "save",
          field: String(title),
        },
        extra: {
          record,
          dataIndex,
        },
      });
    }
  };

  const handlePressEnter = async () => {
    try {
      await form.validateFields([dataIndex]);
      setShowConfirmModal(true);
    } catch (errInfo) {
      console.log("Validation failed:", errInfo);
    }
  };

  let childNode = children;

  if (editable) {
    childNode = editing ? (
      <>
        <div onBlur={cancel}>
          <Form.Item
            style={{ margin: 0 }}
            name={dataIndex}
            rules={[
              {
                required: true,
                message: `Subcontractor display name cannot be empty`,
              },
              {
                validator: (_, value) => {
                  if (value && value.trim() === "") {
                    return Promise.reject(
                      new Error(`Subcontractor display name cannot be empty`),
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input ref={inputRef} onPressEnter={handlePressEnter} />
          </Form.Item>
        </div>
        <Modal
          open={showConfirmModal}
          title="Confirm Save"
          onOk={save}
          onCancel={() => setShowConfirmModal(false)}
          okText="Yes"
          cancelText="No"
        >
          <p>
            Are you sure you want to save changes to{" "}
            <strong>{record.displayName || record.name}</strong>?
          </p>
        </Modal>
      </>
    ) : (
      <div onClick={toggleEdit}>{children}</div>
    );
  }
  return <td>{childNode}</td>;
};

export const SubcontractorsList = () => {
  const project = useProject();
  const notify = useNotification();
  const [isModalVisible, setIsModalVisible] = useState(false);

  const { data, loading, error, refetch } = useGetSubcontractorsQuery({
    variables: {
      ...project,
    },
  });

  const [updateSubcontractor] = useUpdateSubcontractorMutation({
    onCompleted: () => {
      notify("Subcontractor updated successfully!", "success");
      refetch(); // Refetch the data to ensure UI is updated
    },
    onError: (err) =>
      notify(`Failed to update subcontractor: ${err.message}`, "error"),
  });

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleSave = async (row: Item) => {
    const updateData = {
      id: row.id,
      name: row.displayName.toLowerCase(),
      displayName: row.displayName,
    };

    await updateSubcontractor({
      variables: {
        tenant: {
          customer: project.customer,
          project: project.project,
          scope: project.scope,
        },
        subcontractor: {
          id: row.id,
          name: updateData.name,
          displayName: updateData.displayName,
        },
      },
    });
  };

  if (loading) {
    return <Spin />;
  }

  if (error) {
    return <div>Error fetching subcontractors</div>;
  }

  const subcontractors = data?.subcontractors ?? [];

  const dataSource: TableData[] = subcontractors.map((subcontractor) => ({
    key: subcontractor.id,
    id: subcontractor.id,
    name: subcontractor.name,
    displayName: subcontractor.displayName || subcontractor.name, // Use displayName if available, otherwise fallback to name
  }));

  const components = {
    body: {
      row: EditableRow,
      cell: EditableCell,
    },
  };

  const columns = COLUMNS.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: Item) => ({
        record,
        editable: col.editable,
        dataIndex: col.dataIndex,
        title: col.title,
        handleSave,
      }),
    };
  });

  // Sort the data source by display name
  const sortedDataSource = [...dataSource].sort((a, b) =>
    (a.displayName || "").localeCompare(b.displayName || ""),
  );

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <Button type="primary" onClick={showModal}>
          Edit Subcontractor Names
        </Button>
      </div>

      <Modal
        title="Subcontractors"
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        width={800}
        bodyStyle={{ maxHeight: "60vh", overflow: "auto", paddingTop: 0 }}
      >
        <Table
          dataSource={sortedDataSource}
          components={components}
          columns={columns}
          pagination={false}
          showHeader={false}
          rowKey={(record) => record.id}
          scroll={{ y: "50vh" }}
        />
      </Modal>
    </>
  );
};
